// Owns the ephemeral grid UX state (search / filters / sort / fullScreen)
// and exposes setters per concern so renderers don't have to wire each
// piece individually. Sort honours single-or-multi semantics:
//   - click a header     → if column already in sort, flip dir; else
//                          replace the entire sort with this one column.
//   - shift+click header → append/flip this column to the existing sort
//                          (multi-column sort).
// Click an already-toggled descending column again → remove from sort.

import { useCallback, useState } from 'react'
import type { FilterSpec, GridUxState, SortSpec } from './gridTypes'

export interface GridStateApi extends GridUxState {
  setSearch: (s: string) => void
  setFilters: (f: FilterSpec[]) => void
  upsertFilter: (f: FilterSpec) => void
  removeFilter: (column: string) => void
  clearFilters: () => void
  toggleSort: (column: string, additive: boolean) => void
  clearSort: () => void
  setFullScreen: (v: boolean) => void
  toggleFullScreen: () => void
}

export function useGridState(): GridStateApi {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FilterSpec[]>([])
  const [sort, setSort] = useState<SortSpec[]>([])
  const [isFullScreen, setFullScreen] = useState(false)

  const upsertFilter = useCallback((f: FilterSpec) => {
    setFilters((prev) => {
      const idx = prev.findIndex(p => p.column === f.column)
      if (idx === -1) return [...prev, f]
      const next = prev.slice()
      next[idx] = f
      return next
    })
  }, [])

  const removeFilter = useCallback((column: string) => {
    setFilters(prev => prev.filter(p => p.column !== column))
  }, [])

  const clearFilters = useCallback(() => setFilters([]), [])

  const toggleSort = useCallback((column: string, additive: boolean) => {
    setSort((prev) => {
      const idx = prev.findIndex(s => s.column === column)
      if (!additive) {
        // Single-column mode: cycle asc → desc → off, replacing the entire sort.
        if (idx === -1) return [{ column, dir: 'asc' }]
        const current = prev[idx]
        if (current.dir === 'asc') return [{ column, dir: 'desc' }]
        return [] // was desc → clear
      }
      // Additive (shift-click)
      if (idx === -1) return [...prev, { column, dir: 'asc' }]
      const current = prev[idx]
      const next = prev.slice()
      if (current.dir === 'asc') {
        next[idx] = { column, dir: 'desc' }
        return next
      }
      // desc → remove this column from the sort
      next.splice(idx, 1)
      return next
    })
  }, [])

  const clearSort = useCallback(() => setSort([]), [])

  const toggleFullScreen = useCallback(() => setFullScreen(v => !v), [])

  return {
    search,
    filters,
    sort,
    isFullScreen,
    setSearch,
    setFilters,
    upsertFilter,
    removeFilter,
    clearFilters,
    toggleSort,
    clearSort,
    setFullScreen,
    toggleFullScreen,
  }
}
